import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { QuotaBar, RelativeTime, SectionCard, ValueWithChange, formatNumber } from '../../../components/design-system/admin';
import type { OverviewVendorRow } from '../../../common/models/admin-overview.model';

const TOP = 8;

/** Every vendor's numbers for the period, busiest first; each row opens the vendor. */
export function VendorLeaderboard({ vendors, className }: { vendors: OverviewVendorRow[]; className?: string }) {
  const { t } = useTranslation();
  const [all, setAll] = useState(false);
  const rows = all ? vendors : vendors.slice(0, TOP);

  return (
    <SectionCard
      title={t('ADMIN_OVERVIEW.VENDORS.TITLE')}
      description={t('ADMIN_OVERVIEW.VENDORS.DESCRIPTION')}
      action={
        <Button variant="ghost" size="sm" asChild>
          <Link to="/vendors">{t('ADMIN_SHELL.COMMON.VIEW_ALL')}</Link>
        </Button>
      }
      className={className}
      flush
      testId="overview-vendors"
    >
      <div className="overflow-x-auto border-t">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="ps-4">{t('ADMIN_OVERVIEW.VENDORS.COL_VENDOR')}</TableHead>
              <TableHead>{t('ADMIN_OVERVIEW.VENDORS.COL_PLAN')}</TableHead>
              <TableHead className="text-end">{t('ADMIN_OVERVIEW.VENDORS.COL_VISUALIZATIONS')}</TableHead>
              <TableHead className="text-end">{t('ADMIN_OVERVIEW.VENDORS.COL_LEADS')}</TableHead>
              <TableHead className="text-end">{t('ADMIN_OVERVIEW.VENDORS.COL_PRODUCTS')}</TableHead>
              <TableHead>{t('ADMIN_OVERVIEW.VENDORS.COL_QUOTA')}</TableHead>
              <TableHead className="pe-4">{t('ADMIN_OVERVIEW.VENDORS.COL_LAST_ACTIVITY')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((v) => {
              return (
                <TableRow key={v.id} className={cn(!v.isEnabled && 'text-muted-foreground')} data-testid="overview-vendor-row">
                  <TableCell className="ps-4">
                    <Link to={`/vendors/${v.id}/edit`} className="block min-w-0 hover:underline">
                      <span className="font-medium">{v.name}</span>
                      <span className="ms-1.5 text-xs text-muted-foreground">@{v.alias}</span>
                    </Link>
                    {!v.isEnabled && <Badge variant="outline" className="mt-0.5">{t('ADMIN_OVERVIEW.VENDORS.DISABLED')}</Badge>}
                  </TableCell>
                  <TableCell className="text-sm">{v.planName || '—'}</TableCell>
                  <TableCell className="text-end">
                    <ValueWithChange value={v.visualizations} previous={v.visualizationsPrevious} />
                  </TableCell>
                  <TableCell className="text-end tabular-nums">{formatNumber(v.leads)}</TableCell>
                  <TableCell className="text-end tabular-nums">{formatNumber(v.products)}</TableCell>
                  <TableCell>
                    <QuotaBar used={v.quota.used} limit={v.quota.limit} />
                  </TableCell>
                  <TableCell className="pe-4 text-sm text-muted-foreground">
                    <RelativeTime value={v.lastActivityAt} />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      {vendors.length > TOP && (
        <div className="border-t px-2 py-1.5">
          <Button variant="ghost" size="sm" className="w-full text-muted-foreground" onClick={() => setAll((a) => !a)}>
            {all ? t('ADMIN_OVERVIEW.VENDORS.SHOW_TOP') : t('ADMIN_OVERVIEW.VENDORS.SHOW_ALL', { count: vendors.length })}
          </Button>
        </div>
      )}
    </SectionCard>
  );
}
